import React, { useEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { MdCropSquare, MdRadioButtonUnchecked, MdVideocam, MdVisibility } from 'react-icons/md';
import { Box, Text } from '@chakra-ui/react';

// Generic types for any streaming provider
export interface StreamingRoom {
  name: string;
  localParticipant?: {
    identity: string;
  };
  remoteParticipants?: Map<string, unknown>;
}

export interface StreamingTrack {
  kind: string;
  source: string;
  attach: (element: HTMLVideoElement) => void;
  mediaStreamTrack?: MediaStreamTrack;
}

export interface StreamingPiPProps {
  room: StreamingRoom;
  isVisible: boolean;
  onClose: () => void;
  mode: 'stream' | 'join';
  hasVideo?: boolean;
  viewerTheme?: 'normal' | 'mini';
  onAttachTrack?: (track: StreamingTrack) => void;
  onSetupEvents?: (
    room: StreamingRoom,
    videoElement: HTMLVideoElement,
    mode: 'stream' | 'join'
  ) => () => void;
}

export const StreamingPiP: React.FC<StreamingPiPProps> = ({
  room,
  isVisible,
  onClose: _onClose,
  mode,
  hasVideo = false,
  viewerTheme = 'mini',
  onAttachTrack: _onAttachTrack,
  onSetupEvents,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [localTheme, setLocalTheme] = useState(viewerTheme);

  // Track current position for drag & drop
  const [position, setPosition] = useState({ x: 0, y: 0 });

  // Debug logs
  console.log('🔍 StreamingPiP Debug:', {
    isVisible,
    mode,
    hasVideo,
    viewerTheme,
    roomName: room?.name,
    hasOnSetupEvents: !!onSetupEvents,
  });

  useEffect(() => {
    if (!room || !videoRef.current || !isVisible) return;

    console.log('🔍 Setting up PiP events for mode:', mode);

    if (onSetupEvents && videoRef.current) {
      return onSetupEvents(room, videoRef.current, mode);
    }
  }, [room, isVisible, mode, onSetupEvents]);

  useEffect(() => {
    setLocalTheme(viewerTheme);
  }, [viewerTheme]);

  // Handle theme toggle with position reset
  const handleThemeToggle = () => {
    const newTheme = localTheme === 'mini' ? 'normal' : 'mini';

    if (newTheme === 'normal' && localTheme === 'mini') {
      // Switching from mini to normal: reset to top right
      setPosition({ x: 0, y: 0 });
    } else if (newTheme === 'mini' && localTheme === 'normal') {
      // Switching from normal to mini: reset to top right
      setPosition({ x: 0, y: 0 });
    }

    setLocalTheme(newTheme);
    console.log('Theme toggle clicked! Current theme:', newTheme);
  };

  if (!isVisible || !room) return null;

  // Streamer mode: always mini, fixed position
  if (mode === 'stream') {
    console.log('🎥 Rendering STREAMER mode (mini, fixed)');
    return (
      <Box
        position="fixed"
        top={16}
        right={6}
        width="120px"
        height="120px"
        borderRadius="50%"
        bg="black"
        boxShadow="lg"
        zIndex={1000}
        overflow="hidden"
        border="2px solid white"
      >
        <video
          ref={videoRef}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          autoPlay
          muted
          playsInline
        />
        {!hasVideo && (
          <Box
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            color="white"
            textAlign="center"
            fontSize="xs"
          >
            <MdVideocam size={24} />
          </Box>
        )}
      </Box>
    );
  }

  // Viewer mode: hybrid approach - CSS transitions + Draggable for drag & drop
  const isMini = localTheme === 'mini';
  const [isHovered, setIsHovered] = useState(false);

  console.log('🎥 Rendering VIEWER mode:', { isMini, localTheme, viewerTheme });

  // Determine if the component should be draggable
  const isDraggable = !isMini; // Only draggable in normal mode

  // Base container props with CSS transitions
  const containerProps = {
    ref: containerRef,
    width: isMini ? '120px' : '300px',
    height: isMini ? '120px' : '200px',
    top: 16,
    right: 6,
    borderRadius: isMini ? '50%' : 'lg',
    bg: 'black',
    boxShadow: 'lg',
    zIndex: 1000,
    cursor: isMini ? 'default' : 'grab',
    overflow: 'hidden',
    border: '3px solid',
    position: 'fixed' as const,
    borderColor: hasVideo ? 'white' : 'orange.500',
    // Smooth transitions for size and shape changes
    transition: 'width 0.3s ease, height 0.3s ease, border-radius 0.3s ease',
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
  };

  return (
    <Draggable
      nodeRef={containerRef}
      bounds="body"
      // Disable dragging when in mini mode
      disabled={!isDraggable}
      // Control position programmatically
      position={position}
      // Update position when dragged
      onStop={(e, data) => {
        setPosition({ x: data.x, y: data.y });
      }}
    >
      <Box {...containerProps}>
        <video
          ref={videoRef}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: isMini ? '50%' : '0',
            transition: 'width 0.3s ease, height 0.3s ease, border-radius 0.3s ease',
          }}
          autoPlay
          muted
          playsInline
        />

        {/* Theme toggle icon - positioned based on theme */}
        <Box
          position="absolute"
          top={isMini ? '50%' : 'auto'}
          left={isMini ? '50%' : 'auto'}
          bottom={isMini ? 'auto' : '8px'}
          right={isMini ? 'auto' : '8px'}
          transform={isMini ? 'translate(-50%, -50%)' : 'none'}
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="center"
          cursor="pointer"
          onClick={handleThemeToggle}
          zIndex={1001}
          bg="rgba(0,0,0,0.8)"
          borderRadius="lg"
          padding={1}
          minWidth={isMini ? '80px' : '60px'}
          opacity={isHovered ? 1 : 0}
          transition="opacity 0.2s ease"
          _hover={{ bg: 'rgba(0,0,0,0.9)' }}
          // Ensure proper touch target size without forcing it
          minH="auto"
          minW="auto"
          // Add touch-friendly styles
          userSelect="none"
          sx={{ touchAction: 'manipulation' }}
        >
          {isMini ? (
            <>
              <MdCropSquare size={20} color="white" style={{ marginRight: '8px' }} />
              <Text fontSize="10px" color="white">
                enlarge
              </Text>
            </>
          ) : (
            <>
              <MdRadioButtonUnchecked size={16} color="white" style={{ marginRight: '4px' }} />
              <Text fontSize="9px" color="white">
                mini
              </Text>
            </>
          )}
        </Box>

        {/* Informational text - only in normal mode */}
        {!isMini && (
          <Box
            position="absolute"
            bottom="8px"
            left="8px"
            color="white"
            fontSize="9px"
            opacity={isHovered ? 1 : 0}
            transition="opacity 0.2s ease"
            textShadow="1px 1px 2px rgba(0,0,0,0.8)"
          >
            <Text>click & drag to move video</Text>
          </Box>
        )}

        {/* Status info */}
        {!hasVideo && (
          <Box
            position="absolute"
            top={isMini ? '50%' : 3}
            left={isMini ? '50%' : 3}
            right={isMini ? 'auto' : 3}
            color="white"
            fontSize="xs"
            textShadow="1px 1px 2px rgba(0,0,0,0.8)"
            transform={isMini ? 'translate(-50%, -50%)' : 'none'}
            textAlign={isMini ? 'center' : 'left'}
          >
            <Text fontWeight="bold" display="flex">
              <MdVisibility size={16} style={{ display: 'inline', marginRight: '4px' }} />
              {isMini ? '' : 'Waiting for video...'}
            </Text>
            {!isMini && <Text mt={1}>Room: {room.name}</Text>}
          </Box>
        )}
      </Box>
    </Draggable>
  );
};
